// © Microsoft Corporation. All rights reserved.

import AbortController from "abort-controller";
import Agent from "agentkeepalive";
import { canUseDOM } from "@msnews/isomorphic-util";
import { logger } from "@msnews/diagnostics";

// Keep alive agents will automatically keep outbound connections open to reduce resource usage.
let httpAgent: Agent;
let httpsAgent: Agent;

/**
 * Augment fetch options that contains node-fetch extensions
 */
declare interface RequestInit {
    // whatwg/fetch standard options
    signal?: AbortSignal | null;

    // node-fetch extensions
    agent?: Agent | ((parsedUrl: URL) => Agent); // =null http.Agent instance, allows custom proxy, certificate etc.
}

/**
 * On server side, initialize abortable fetch with the custom http agents as soon as possible (in node.index.ts)
 * @param inHttpAgent the input http agent
 * @param inHttpsAgent the input https agent
 */
export function abortableFetchInitialize(inHttpAgent: Agent, inHttpsAgent: Agent) {
    if (httpAgent && httpsAgent) {
        return;
    }

    if (inHttpAgent && inHttpsAgent) {
        httpAgent = inHttpAgent;
        httpsAgent = inHttpsAgent;
    } else {
        httpAgent = new Agent();
        httpsAgent = new Agent.HttpsAgent();
    }
}

/**
 * Fetch with timeout and close connection
 * @param url fetch url
 * @param timeoutMs timeout in miliseconds before rejecting the fetch
 * @param init fetch options
 */
export async function abortableFetch(url: string, timeoutMs: number, init?: RequestInit): Promise<Response> {
    const controller: AbortController = new AbortController();

    const timeout = setTimeout(() => {
        controller.abort();
    }, timeoutMs);

    if (!init) {
        init = {};
    }

    // Insert default http agents with keep-alive if not defined (server only)
    if (!canUseDOM() && !init.agent && httpAgent && httpsAgent) {
        const agentFunc = function (_parsedURL) {
            if (_parsedURL.protocol === "http:") {
                return httpAgent;
            } else {
                return httpsAgent;
            }
        };

        init.agent = agentFunc;
    }

    // Insert default abort controller if not defined
    if (!init.signal) {
        init.signal = controller.signal;
    }

    const requestStart = performance.now();
    let errorObj;
    let statusCode = 0;

    return fetch(url, init)
        .then((response) => {
            statusCode = response.status;
            return response;
        })
        .catch(err => {
            errorObj = err;
            if (err.name === "AbortError") {
                throw new Error(`Fetch for ${url} aborted due to timeout of ${timeoutMs} ms.`);
            }
            throw new Error(err);
        })
        .finally(() => {
            if (!canUseDOM()) {
                const serverFetchInfo = {
                    abortableFetch: {
                        url: url,
                        statusCode: statusCode,
                        timeElapsed: (performance.now() - requestStart).toFixed(2),
                        timeOut: timeoutMs.toFixed(2),
                        errorMessage: errorObj && errorObj.message,
                        errorName: errorObj && errorObj.name,
                        errorCode: errorObj && errorObj.code,
                        errorType: errorObj && errorObj.type
                    }
                };
                logger.logObjects(serverFetchInfo);
            }

            clearTimeout(timeout);
        });
}